/**
 * A child's details: the profile fields the step lists, each checked or filled
 * in, and saved to the child's profile when submitted. Plain fields are
 * inputs here; the medical questions are the ones the enrollment forms ask.
 * Answers save as the guardian goes (see useStepAnswers). A finished step
 * reads back what was answered.
 */
import { MedicalForm } from '~/components/form-chunks/StudentMedicalChunk';
import { Input } from '~/components/ui/input';
import { Field, Section, selectClass, StepFooter, StepHeader, type StepProps } from './stepParts';
import { detailWords, FIELD_LABELS, GENDERS, PLAIN_FIELDS, type DetailAnswers } from './childDetailsWords';
import { useStepAnswers } from './useStepAnswers';

function readAnswers(answers: unknown): DetailAnswers {
  return answers && typeof answers === 'object' && !Array.isArray(answers) ? (answers as DetailAnswers) : {};
}

export default function ChildDetailsStep({ step, readOnly, overviewPath, stepPath }: StepProps) {
  const details = step.childDetails;
  if (!details) return null;

  const header = <StepHeader step={step} subtitle={`${details.childName} · ${step.yearName}`} />;

  return (
    <div className="space-y-6">
      {header}
      {readOnly ? (
        <Summary fields={details.fields} answers={readAnswers(step.answers)} />
      ) : (
        <DetailsForm step={step} fields={details.fields} childName={details.childName} returning={details.returning} overviewPath={overviewPath} stepPath={stepPath} />
      )}
    </div>
  );
}

function Summary({ fields, answers }: { fields: string[]; answers: DetailAnswers }) {
  return (
    <Section>
      <dl className="space-y-3 text-sm">
        {fields.map((field) => (
          <div key={field}>
            <dt className="text-muted-foreground">{FIELD_LABELS[field] ?? field}</dt>
            <dd className="break-words">{detailWords(field, answers) || 'Nothing entered'}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function DetailsForm({ step, fields, childName, returning, overviewPath, stepPath }: {
  step: StepProps['step'];
  fields: string[];
  childName: string;
  returning: boolean;
  overviewPath: string;
  stepPath: (itemId: string) => string;
}) {
  const itemId = step.item.id;
  const { answers, change, submit, saveState, errors, submitting } = useStepAnswers<DetailAnswers>({
    itemId,
    initial: readAnswers(step.answers),
    overviewPath,
    stepPath,
  });
  const set = (field: string, value: unknown) => change((current) => ({ ...current, [field]: value }));
  const medicalFields = fields.filter((field) => !PLAIN_FIELDS.includes(field));
  const has = (field: string) => fields.includes(field);
  const text = (field: string) => (typeof answers[field] === 'string' ? (answers[field] as string) : '');

  return (
    <>
      {/* Held still while a submission is on its way. */}
      <fieldset disabled={submitting} className="min-w-0 space-y-6">
        {PLAIN_FIELDS.some(has) ? (
          <Section>
            {has('dateOfBirth') ? (
              <Field id={`${itemId}-dob`} label="Birth date">
                <Input
                  id={`${itemId}-dob`}
                  type="date"
                  className="h-11 md:h-10"
                  value={text('dateOfBirth')}
                  onChange={(event) => set('dateOfBirth', event.target.value)}
                />
              </Field>
            ) : null}
            {has('gender') ? (
              <Field id={`${itemId}-gender`} label="Gender">
                <select
                  id={`${itemId}-gender`}
                  className={selectClass}
                  value={text('gender')}
                  onChange={(event) => set('gender', event.target.value || null)}
                >
                  <option value="">Choose one</option>
                  {GENDERS.map((gender) => <option key={gender.value} value={gender.value}>{gender.label}</option>)}
                </select>
              </Field>
            ) : null}
            {has('currentSchool') ? (
              <Field id={`${itemId}-school`} label="Current school">
                <Input
                  id={`${itemId}-school`}
                  className="h-11 md:h-10"
                  value={text('currentSchool')}
                  onChange={(event) => set('currentSchool', event.target.value)}
                />
              </Field>
            ) : null}
          </Section>
        ) : null}

        {medicalFields.length > 0 ? (
          <MedicalForm
            student={{ id: itemId, firstName: childName, lastName: '', type: returning ? 'returning' : 'new' }}
            info={answers}
            fields={medicalFields}
            onChange={set}
          />
        ) : null}
      </fieldset>

      <StepFooter errors={errors} saveState={saveState} submitting={submitting} label="Save and continue" onSubmit={submit} />
    </>
  );
}
